// One audit's section of the select step's SEARCH view. The batched search
// endpoint returns each audit's first matches plus its full match count; this
// renders that bucket, and "Show all N matches" swaps the section to the
// regular per-audit paginated group (WorkStepAuditGroupLoaded) scoped by the
// same term — which also restores the group select-all for bulk selection.
import { useState } from "react";
import { Button } from "../../../../../components/ui/button";
import { TableCell, TableRow } from "../../../../../components/ui/table";
import {
  OptroOpsAuditSummary,
  OptroWorkStepSearchBucket,
  OptroWorkStepSummary,
} from "../../../../../models/audit/workStep";
import { SelectWorkStepRow } from "./SelectWorkStepRow";
import { WorkStepAuditGroupLoaded } from "./WorkStepAuditGroupLoaded";

const COLUMN_COUNT = 5;

interface WorkStepSearchGroupProps {
  audit: OptroOpsAuditSummary;
  term: string;
  // Undefined until the search response lands (or when the backend skipped
  // the audit because it no longer resolves in Optro).
  bucket: OptroWorkStepSearchBucket | undefined;
  isSelected: (externalRef: string) => boolean;
  onToggle: (workStep: OptroWorkStepSummary) => void;
  selectMany: (workSteps: OptroWorkStepSummary[]) => void;
  deselectMany: (externalRefs: string[]) => void;
}

export const WorkStepSearchGroup = ({
  audit,
  term,
  bucket,
  isSelected,
  onToggle,
  selectMany,
  deselectMany,
}: WorkStepSearchGroupProps) => {
  // Sticky per group: once the user asks for everything, further term
  // refinements keep the full paginated view (it refetches with the term).
  const [showAll, setShowAll] = useState(false);

  if (showAll) {
    return (
      <WorkStepAuditGroupLoaded
        audit={audit}
        search={term}
        isSelected={isSelected}
        onToggle={onToggle}
        selectMany={selectMany}
        deselectMany={deselectMany}
        autoSelectAll={false}
      />
    );
  }

  const rows = bucket?.workSteps ?? [];
  const total = bucket?.total ?? 0;

  return (
    <>
      <TableRow className="bg-stone-50">
        <TableCell />
        <TableCell colSpan={COLUMN_COUNT - 1} className="font-semibold">
          <div className="flex items-center gap-2">
            <span>{audit.name}</span>
            <span className="text-sm font-normal text-stone-500">
              {total} match{total === 1 ? "" : "es"}
            </span>
          </div>
        </TableCell>
      </TableRow>
      {rows.length === 0 && (
        <TableRow>
          <TableCell
            colSpan={COLUMN_COUNT}
            className="py-3 text-center text-sm text-stone-500"
          >
            No work steps match your search in this audit.
          </TableCell>
        </TableRow>
      )}
      {rows.map((workStep) => (
        <SelectWorkStepRow
          key={workStep.externalRef}
          workStep={workStep}
          checked={isSelected(workStep.externalRef)}
          onToggle={onToggle}
        />
      ))}
      {total > rows.length && (
        <TableRow>
          <TableCell colSpan={COLUMN_COUNT} className="py-1 text-center">
            <Button variant="link" size="sm" onClick={() => setShowAll(true)}>
              Show all {total} matches
            </Button>
          </TableCell>
        </TableRow>
      )}
    </>
  );
};
